/**
 * The real sync transport — a module API client over AuthUtil, the same pattern
 * as recipeCostApi.js and the hive/*Api.js wrappers. No redux: this is called
 * by the sync engine, not by components.
 *
 * Workspace scoping is explicit, as everywhere in BeeNexa: there is no axios
 * interceptor and no header, so every payload carries
 * `companyId: getSpaceId(userInfo)` and the server passes it through
 * resolveSpaceId before scoping anything (repo-root CLAUDE.md).
 */

import axios from 'axios';
import AuthUtil from '../../redux/AuthUtil';
import { FAIR_TRACKER } from '../global/Server/endpoints';
import { getSpaceId } from '../../hive/getSpaceId';

/**
 * The current user, supplied by the store via `configure`.
 *
 * It has to be injected rather than read from redux here, because this module
 * is called by the sync engine — which is not a component and has no store
 * access. Without it, getSpaceId(null) would fall back to null and leave the
 * server to guess the home space; explicit is better than relying on that.
 */
let currentUser = null;

export function configure(userInfo) {
  currentUser = userInfo || null;
}

/**
 * The active space id. Read fresh on every call rather than captured once —
 * switching workspace reloads the page, but a long-lived offline session can
 * outlive that assumption.
 */
const spaceId = () => getSpaceId(currentUser) || undefined;

const post = async (endpoint, data) => {
  const res = await AuthUtil.postRequest(endpoint, { ...data, companyId: spaceId() });
  return res && res.data;
};

/**
 * Delta pull + push in one round trip.
 *
 * Resolves to the shape the sync engine expects:
 *   { fairs[], suppliers[], settings|null, serverTime }
 * and REJECTS on a transport or application failure, so the engine leaves the
 * outbox intact and retries later. A partially-applied batch comes back with
 * `rejected[]` — those entries stay queued rather than being silently dropped.
 */
export async function sync({ since, changes }) {
  const body = await post(FAIR_TRACKER.SYNC, { since, changes });
  if (!body || body.success === false) {
    throw new Error((body && body.message) || 'Sync failed');
  }
  return {
    fairs: body.fairs || [],
    suppliers: body.suppliers || [],
    settings: body.settings || null,
    serverTime: body.serverTime || Date.now(),
    applied: body.applied || 0,
    rejected: body.rejected || [],
  };
}

export async function listAll() {
  const body = await post(FAIR_TRACKER.LIST_ALL, {});
  if (!body || body.success === false) {
    throw new Error((body && body.message) || 'Could not load');
  }
  return body;
}

/**
 * Upload one captured file as multipart.
 *
 * Deliberately NOT through AuthUtil: its helpers force
 * `Content-Type: application/json`, and multipart needs the browser to set its
 * own boundary. The Authorization header is attached the same way AuthUtil
 * does it, so auth behaviour is identical.
 */
export async function uploadMedia({
  mediaId,
  supplierId,
  productId,
  contactId,
  kind,
  tag,
  blob,
  filename,
  onProgress,
}) {
  const form = new FormData();
  form.append('file', blob, filename);
  form.append('mediaId', mediaId);
  form.append('kind', kind);
  if (supplierId) form.append('supplierId', supplierId);
  if (productId) form.append('productId', productId);
  if (contactId) form.append('contactId', contactId);
  if (tag) form.append('tag', tag);
  const space = spaceId();
  if (space) form.append('companyId', space);

  const res = await axios.post(FAIR_TRACKER.UPLOAD_MEDIA, form, {
    headers: { Authorization: `Bearer ${AuthUtil.getToken()}` },
    onUploadProgress: onProgress,
  });
  const body = res && res.data;
  if (!body || body.success === false) {
    throw new Error((body && body.message) || 'Upload failed');
  }
  return { path: body.path, size: body.size };
}

export async function deleteMedia(mediaId) {
  const body = await post(FAIR_TRACKER.DELETE_MEDIA, { mediaId });
  return !!(body && body.success);
}

/**
 * What the sync engine consumes.
 *
 * `isOnline` is navigator.onLine, which is a hint rather than a guarantee —
 * a Canton Fair hall can report "online" on a captive wifi that routes
 * nowhere. That is fine: a failed sync costs nothing because the outbox is
 * only settled on success.
 */
export const transport = {
  isOnline: () => (typeof navigator === 'undefined' ? true : navigator.onLine !== false),
  sync,
  uploadMedia,
};

export default transport;
